import { Task } from "@common/types/task";
import { useCallback, useMemo } from "react";
import { useTaskBlockingSection } from "./useTaskBlockingSection";
import { TaskBlockingList } from "./TaskBlockingList";

interface RelatedSectionProps {
    task: Task;
    onRelatedChange?: (relatedTaskIds: string[]) => Promise<unknown>;
}

export const RelatedSection = ({
    task,
    onRelatedChange,
}: Readonly<RelatedSectionProps>) => {
    const { taskSummaries, goToTask, addOpen, setAddOpen } =
        useTaskBlockingSection(task);

    const relatedTaskIds = useMemo(
        () => task.relatedTaskIds ?? [],
        [task.relatedTaskIds],
    );

    const availableToAdd = useMemo(
        () =>
            taskSummaries.filter(
                (t) => t.id !== task.id && !relatedTaskIds.includes(t.id),
            ),
        [taskSummaries, task.id, relatedTaskIds],
    );

    const removeRelated = useCallback(
        (id: string) => {
            onRelatedChange?.(relatedTaskIds.filter((taskId) => taskId !== id));
        },
        [onRelatedChange, relatedTaskIds],
    );

    const addRelated = useCallback(
        (taskId: string) => {
            onRelatedChange?.([...relatedTaskIds, taskId]);
        },
        [onRelatedChange, relatedTaskIds],
    );

    return (
        <TaskBlockingList
            taskIds={relatedTaskIds}
            availableToAdd={availableToAdd}
            onAdd={addRelated}
            onRemove={removeRelated}
            addOpen={addOpen}
            setAddOpen={setAddOpen}
            goToTask={goToTask}
            emptyLabel="None"
        />
    );
};
